// pages/PublicFormPage.jsx
import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../utils/firebase";
import {
  doc,
  getDoc,
  addDoc,
  collection,
  Timestamp,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import FormRenderer from "../components/FormRenderer";

function PublicFormPage() {
  const { orgId, formId } = useParams();
  const navigate = useNavigate();
  const containerRef = useRef(null);

  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEmbedded, setIsEmbedded] = useState(false);

  // Detect if running in an iframe
  useEffect(() => {
    setIsEmbedded(window !== window.top);
  }, []);

  // Function to communicate height to parent window
  const adjustIframeHeight = () => {
    if (isEmbedded && containerRef.current) {
      const height =
        containerRef.current.scrollHeight || document.body.scrollHeight;
      window.parent.postMessage({ type: "setHeight", height }, "*");
    }
  };

  useEffect(() => {
    const fetchForm = async () => {
      try {
        const formRef = doc(db, `organizations/${orgId}/forms`, formId);
        const formSnap = await getDoc(formRef);
        if (formSnap.exists()) {
          setForm({ id: formSnap.id, ...formSnap.data() });
        } else {
          console.error("Form not found");
        }
      } catch (err) {
        console.error("Error fetching form:", err);
      } finally {
        setLoading(false);

        // If embedded, notify parent about initial height after form loads
        if (isEmbedded) {
          setTimeout(adjustIframeHeight, 200);
        }
      }
    };
    fetchForm();
  }, [orgId, formId, isEmbedded]);

  // Add window resize listener if embedded
  useEffect(() => {
    if (isEmbedded) {
      window.addEventListener("resize", adjustIframeHeight);
      return () => window.removeEventListener("resize", adjustIframeHeight);
    }
  }, [isEmbedded]);

  // Handle form submission
  const handleSubmit = async (submissionData) => {
    try {
      // Ensure header images and textboxes are properly handled
      const processedData = { ...submissionData };

      // Make sure responses exists
      if (!processedData.responses) {
        processedData.responses = {};
      }

      // Process header images and textboxes
      form.elements.forEach((element) => {
        const uniqueId = element.uniqueId;

        // For header images
        if (
          element.type === "headerImage" &&
          !processedData.responses[uniqueId]
        ) {
          processedData.responses[uniqueId] = {
            imageUrl: element.imageUrl,
            fileName: element.fileName,
            size: element.size || "medium",
            alignment: element.alignment || "center",
            customHeight: element.customHeight || null, // Prevent undefined
          };
        }

        // For textboxes
        if (element.type === "textBox" && !processedData.responses[uniqueId]) {
          processedData.responses[uniqueId] = element.content || "";
        }
      });

      // Add metadata
      processedData.formId = formId;
      processedData.formName = form.name;
      processedData.dateSubmitted = Timestamp.now();

      // Find applicant name field and extract full name if available
      let applicantFullName = "";
      let applicantIdentifierFound = false;

      // Loop through elements to find the applicant identifier field
      for (const element of form.elements) {
        if (element.isApplicantIdentifier || element.type === "applicantName") {
          // We found the applicant identifier field
          applicantIdentifierFound = true;
          const uniqueId = element.uniqueId;
          applicantFullName = processedData.responses[uniqueId] || "";
          break;
        }
      }

      let residentId = null;
      let matchedResident = null;

      // If we found an applicant name, try to match it with a resident
      if (applicantIdentifierFound && applicantFullName) {
        try {
          // Query the residents collection to find a matching resident
          const residentsRef = collection(
            db,
            `organizations/${orgId}/residents`
          );
          const q = query(
            residentsRef,
            where("fullName", "==", applicantFullName)
          );
          const residentSnapshot = await getDocs(q);

          if (!residentSnapshot.empty) {
            // We found a matching resident
            matchedResident = residentSnapshot.docs[0];
            residentId = matchedResident.id;

            // Add resident info to the submission data
            processedData.residentId = residentId;
            processedData.residentFullName = applicantFullName;

            console.log(
              `Matched submission to resident: ${applicantFullName} (ID: ${residentId})`
            );
          } else {
            console.log(
              `No resident found with fullName: ${applicantFullName}`
            );
          }
        } catch (error) {
          console.error("Error finding matching resident:", error);
          // Continue with submission even if resident matching fails
        }
      }

      // Keep the original approach of adding documents one by one

      // 1. Save to the form's submissions collection first
      console.log("Saving to form submissions collection");
      const formSubmissionRef = await addDoc(
        collection(db, `organizations/${orgId}/forms/${formId}/submissions`),
        processedData
      );

      console.log("Form submission saved with ID:", formSubmissionRef.id);

      // 2. If it's an application, also save to applications collection
      if (form.isApplication) {
        console.log("Saving to applications collection");
        await addDoc(collection(db, `organizations/${orgId}/applications`), {
          ...processedData,
          // Add a reference to the original submission
          formSubmissionId: formSubmissionRef.id,
        });
      }

      // 3. If we matched a resident, also save to resident's formSubmissions collection
      if (residentId) {
        console.log("Saving to resident's formSubmissions collection");
        await addDoc(
          collection(
            db,
            `organizations/${orgId}/residents/${residentId}/formSubmissions`
          ),
          {
            ...processedData,
            // Add a reference to the original submission
            formSubmissionId: formSubmissionRef.id,
          }
        );
        console.log(
          `Saved submission to resident's formSubmissions collection`
        );
      }

      // Handle UI feedback
      if (isEmbedded) {
        window.parent.postMessage({ type: "formSubmitted" }, "*");
        setTimeout(adjustIframeHeight, 100);
      } else {
        alert("Thanks for submitting the form!");
        navigate("/welcome");
      }

      return true;
    } catch (error) {
      console.error("Error saving submission:", error);
      alert("Error submitting form. Please try again.");
      return Promise.reject(error);
    }
  };

  if (loading) {
    return (
      <div
        ref={containerRef}
        className="p-6 text-center bg-slate-900"
        style={isEmbedded ? { margin: 0, borderRadius: 0 } : {}}
      >
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
        <p className="mt-4 text-white">Loading form...</p>
      </div>
    );
  }

  if (!form) {
    return (
      <div
        ref={containerRef}
        className="p-6 text-center bg-slate-900"
        style={isEmbedded ? { margin: 0, borderRadius: 0 } : {}}
      >
        <p className="text-red-500">Form not found</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={
        isEmbedded ? "p-0 bg-slate-900" : "p-6 bg-slate-900 max-w-4xl mx-auto"
      }
      style={isEmbedded ? { margin: 0, borderRadius: 0 } : {}}
    >
      <FormRenderer
        form={form}
        onSubmit={handleSubmit}
        onClose={() => navigate("/welcome")}
        isModal={!isEmbedded} // Use modal display for direct access, non-modal for embedded
        onHeightChange={isEmbedded ? adjustIframeHeight : null} // Only pass height adjustment for embedded
      />
    </div>
  );
}

export default PublicFormPage;
